/**
 * Display labels for numbered Ace trial runs.
 */

import type { AceTrialSummary } from "@/services/api/agentReview";
import { ragOption } from "./RagOption";
import type { AceProfile, Rag } from "./types";

const ACE_PROFILE_LABELS: Record<AceProfile, string> = {
  strict: "Strict",
  balanced: "Balanced",
  lenient: "Lenient",
};

export function aceProfileLabel(profile: AceProfile | null | undefined): string {
  if (!profile || profile === "strict") return ACE_PROFILE_LABELS.strict;
  return ACE_PROFILE_LABELS[profile] ?? profile;
}

export function aceTrialRunBadge(runNumber?: number): string {
  return runNumber ? `Run #${runNumber}` : "Run";
}

export function aceTrialTitle(
  trial: Pick<AceTrialSummary, "run_number" | "note" | "criteria_version_label" | "ace_profile">,
): string {
  const badge = trial.run_number ? aceTrialRunBadge(trial.run_number) : null;
  const profileSuffix =
    trial.ace_profile && trial.ace_profile !== "strict"
      ? ` (${aceProfileLabel(trial.ace_profile)})`
      : "";
  const detail = trial.note?.trim() || trial.criteria_version_label || "Ace trial";
  if (badge && detail.startsWith(badge)) {
    return detail.includes(profileSuffix.trim()) ? detail : `${detail}${profileSuffix}`;
  }
  return badge ? `${badge}${profileSuffix} — ${detail}` : `${detail}${profileSuffix}`;
}

export function aceTrialOptionLabel(
  trial: Pick<
    AceTrialSummary,
    "run_number" | "note" | "criteria_version_label" | "ace_profile" | "is_canonical" | "orch_rag" | "out_rag"
  >,
): string {
  const rags = [
    trial.orch_rag ? ragOption(trial.orch_rag).label : "—",
    trial.out_rag ? ragOption(trial.out_rag).label : "—",
  ].join(" / ");
  const primary = trial.is_canonical ? " · primary" : "";
  return `${aceTrialTitle(trial)} — ${rags}${primary}`;
}

export function aceTrialRagPair(orch: Rag | null, out: Rag | null): string {
  const o = orch ? ragOption(orch).label : "—";
  const u = out ? ragOption(out).label : "—";
  return `${o} / ${u}`;
}
